/**
 * MetaStoryCarouselMockup - Instagram/Facebook Story/Reels 9:16 Carousel Ad Mockup
 *
 * Dimensions: 350px × 622px (9:16 aspect ratio)
 *
 * Structure (matching Instagram Story carousel ad format):
 * - Top Progress Bars: Multiple bars showing carousel position
 * - Header: Circular profile picture, username, "Sponsored" label, close button
 * - Full-screen Background: Current 9:16 slide image
 * - Navigation: Left/right arrows on hover
 * - Right Side Actions: Heart, message, share icons
 * - Bottom CTA: Swipe-up style button
 *
 * Usage:
 *   <MetaStoryCarouselMockup
 *     username="Ford"
 *     items={[
 *       { imageUrl: '/path/to/image1.jpg', headline: 'Slide 1' },
 *       { imageUrl: '/path/to/image2.jpg', headline: 'Slide 2' },
 *     ]}
 *     ctaText="Learn More"
 *   />
 */

import { useState, useEffect, useRef } from 'react';
import { MediaDisplay } from './MediaDisplay';

interface CarouselItem {
  imageUrl?: string;
  contentType?: string | null;
  headline?: string;
  cta?: string;
}

interface MetaStoryCarouselMockupProps {
  username?: string;
  profilePicUrl?: string;
  items?: CarouselItem[];
  ctaText?: string;
  websiteUrl?: string;
  activeIndex?: number;
  onSlideChange?: (index: number) => void;
}

export function MetaStoryCarouselMockup({
  username = 'Ford',
  profilePicUrl,
  items = [
    { imageUrl: '', headline: 'Slide 1' },
    { imageUrl: '', headline: 'Slide 2' },
    { imageUrl: '', headline: 'Slide 3' },
  ],
  ctaText = 'Learn More',
  websiteUrl = 'ford.com',
  activeIndex,
  onSlideChange,
}: MetaStoryCarouselMockupProps) {
  const [currentIndex, setCurrentIndex] = useState(activeIndex ?? 0);
  const isSyncingFromParent = useRef(false);

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== currentIndex) {
      isSyncingFromParent.current = true;
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSyncingFromParent.current) {
      isSyncingFromParent.current = false;
      return;
    }
    onSlideChange?.(currentIndex);
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const currentItem = items[currentIndex];

  return (
    <div className="relative w-full h-full bg-black overflow-hidden group">
      {/* Full-screen Background Image/Video */}
      <div className="absolute inset-0">
        {currentItem?.imageUrl ? (
          <MediaDisplay
            url={currentItem.imageUrl}
            contentType={currentItem.contentType}
            alt={currentItem.headline || `Slide ${currentIndex + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <div className="text-center text-white/50">
              <div className="text-4xl font-bold mb-2">9:16</div>
              <div className="text-sm">Slide {currentIndex + 1} of {items.length}</div>
              <div className="text-xs mt-1">1080x1920px recommended</div>
            </div>
          </div>
        )}
      </div>

      {/* Gradient overlays for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />

      {/* Story progress bars at very top - one per slide */}
      <div className="absolute top-2 left-3 right-3 z-30 flex gap-1">
        {items.map((_, idx) => (
          <div
            key={idx}
            className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden cursor-pointer"
            onClick={() => setCurrentIndex(idx)}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                idx < currentIndex
                  ? 'w-full bg-white'
                  : idx === currentIndex
                  ? 'w-full bg-white'
                  : 'w-0 bg-white'
              }`}
            />
          </div>
        ))}
      </div>

      {/* Top Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between"
        style={{ padding: '16px 12px 0' }}
      >
        {/* Left: Profile & Username */}
        <div className="flex items-center" style={{ gap: '10px' }}>
          {/* Profile Picture - Circular with gradient border */}
          <div
            className="rounded-full p-[2px]"
            style={{
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
            }}
          >
            <div
              className="bg-black rounded-full overflow-hidden flex items-center justify-center"
              style={{ width: '32px', height: '32px' }}
            >
              {profilePicUrl ? (
                <img
                  src={profilePicUrl}
                  alt={username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-sm">
                  {username.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Username & Sponsored */}
          <div className="flex items-center gap-2">
            <span
              className="text-white font-semibold"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              {username}
            </span>
            <span className="text-white/80" style={{ fontSize: '13px' }}>
              •
            </span>
            <span
              className="text-white/80"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              Sponsored
            </span>
          </div>
        </div>

        {/* Right: Close button */}
        <button className="text-white/90 hover:text-white">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Navigation Arrows - Show on hover */}
      {items.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      {/* Right Side Actions */}
      <div
        className="absolute z-20 flex flex-col items-center"
        style={{ right: '12px', bottom: '120px', gap: '20px' }}
      >
        {/* Heart */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Message */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Share/Send */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Bottom Section */}
      <div className="absolute bottom-0 left-0 right-0 z-20" style={{ padding: '0 12px 16px' }}>
        {/* Headline for current slide — commented out: 9:16 story carousel cards don't have a headline
        {currentItem?.headline && (
          <p
            className="text-white text-sm mb-3 font-semibold"
            style={{ fontSize: '14px', lineHeight: '18px' }}
          >
            {currentItem.headline}
          </p>
        )} */}

        {/* CTA Button - Instagram Story Style */}
        <div className="flex flex-col items-center">
          {/* Swipe up indicator */}
          <div className="flex flex-col items-center mb-2">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="animate-bounce"
            >
              <path
                d="M12 19V5M5 12L12 5L19 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* CTA Button */}
          <button
            className="w-full flex items-center justify-center text-white font-semibold"
            style={{
              height: '44px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(8px)',
              borderRadius: '22px',
              fontSize: '15px',
              letterSpacing: '0.3px',
            }}
          >
            {currentItem?.cta || ctaText}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2"
            >
              <path
                d="M5 12H19M12 5L19 12L12 19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Website URL below button */}
          <span
            className="text-white/70 mt-2"
            style={{ fontSize: '12px' }}
          >
            {websiteUrl}
          </span>
        </div>
      </div>

      {/* Slide counter */}
      <div
        className="absolute z-20 bg-black/50 rounded-full px-2 py-1"
        style={{ top: '50px', right: '12px' }}
      >
        <span className="text-white text-xs font-medium">
          {currentIndex + 1}/{items.length}
        </span>
      </div>
    </div>
  );
}
